import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal, viewChild } from '@angular/core';
import { CartSummaryComponent } from '../old/cart-summary/cart-summary.component';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, filter, from, lastValueFrom, map, of, switchMap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { calculatePriceWithCoupon, humanize, ICouponUse, IPackage, sortCouponsByValue } from '../types';
import { backend } from '../../environments/environment';
import { IHandleResponse, ProfileService } from '../old/profile/profile.service';
import { LocationService } from '../location.service';
import { CouponListComponent } from '../old/coupon-list/coupon-list.component';
import { MatButtonModule } from '@angular/material/button';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { PaintDirective } from '../paint.directive';
import { ToastrService } from 'ngx-toastr';
import { MatError, MatHint } from '@angular/material/form-field';
import { AuthService } from '../auth.service';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmPopup } from '../confirm.popup';
import { CardInputComponent, CardService } from "../card-input/card-input.component";
import { PaymentIntent } from '@stripe/stripe-js';
import { Analytics, logEvent } from '@angular/fire/analytics';

@Component({
  selector: 'package-checkout',
  standalone: true,
  imports: [CartSummaryComponent, MatButtonModule, MatIconModule, PaintDirective, ReactiveFormsModule, MatHint, MatError, MatDividerModule, MatSlideToggleModule, RouterLink, CardInputComponent],
  templateUrl: './package-checkout.component.html',
  styleUrl: './package-checkout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export default class PackageCheckoutComponent {
  http = inject(HttpClient);
  profile = inject(ProfileService);
  router = inject(LocationService);
  destroy = inject(DestroyRef);
  confirm = inject(ConfirmPopup);
  sheets = inject(MatBottomSheet);
  toast = inject(ToastrService);
  auth = inject(AuthService);
  activated = inject(ActivatedRoute);
  card = inject(CardService);
  ga = inject(Analytics);

  cartSummary = viewChild<CartSummaryComponent>(CartSummaryComponent);
  cardInput = viewChild<CardInputComponent>(CardInputComponent);

  plan = input.required<string>();

  package = signal<IPackage|null>(null);
  uses = signal<ICouponUse[]>([]);
  packages = signal<IPackage[]>([]);
  coupon_state = signal<'success'|'pending'|'failed'|'idle'>('idle');
  isLoading = signal<boolean>(false);
  selected_use_id = signal<string|undefined>(undefined);
  
  selected_use = computed(() => {
    const _id = this.selected_use_id();
    if(!_id) return null;
    return this.uses().find(use => use._id == _id);
  });
  email_verified = computed(() => {
    const session = this.auth.userSession();
    return session ? session.email_verified : true
  });
  payment_method_valid = computed(() => {
    const card_input = this.cardInput();
    const cart_summary = this.cartSummary();
    const selected_payment_method_id = card_input?.selected_method_id();
    const current_state = card_input?.selected_view();
    const retailable = cart_summary?.retailable();
    const no_card_required = cart_summary?.no_card_required();

    if(!cart_summary || !card_input) return false;

    if(!retailable || no_card_required){
      return true;
    }
    
    if(selected_payment_method_id && ['selected', 'list'].includes(current_state!)){
      return true;
    }

    if(current_state == 'add'){
      return true;
    }

    return false;
  });
  block_transaction = computed(() => {
    // console.log(this.coupon_state() == 'pending', !this.uses(), this.isLoading(), !this.payment_method_valid(), !this.email_verified(), !this.email_valid());
    return this.coupon_state() == 'pending' || !this.uses() || this.isLoading() || !this.payment_method_valid() || (!this.email_verified() && !this.email_valid());
  });
  package_uses = computed(() => {
    const uses = this.uses();
    const pkg = this.package();

    if(!pkg) return [];

    return uses.filter(use => use.coupon.package == pkg._id);
  });
  codes = computed(() => this.uses().map(use => use.code.toLowerCase()));

  code = new FormControl('', { validators: [Validators.required], nonNullable: true });
  email = new FormControl('', { validators: [Validators.required], nonNullable: true });
  terms = new FormControl(false, { validators: [Validators.requiredTrue], nonNullable: true });

  email_valid = toSignal(this.email.valueChanges.pipe(map(v => this.email.valid)));

  constructor(){
    toObservable(this.coupon_state).pipe(filter((v) => ['success','idle'].includes(v)), takeUntilDestroyed(), debounceTime(1500)).subscribe(() => {
      const pkg = this.package();
      
      if(pkg){
        const use = this.selected_use();
  
        const base_price = Number(pkg.price || '0.00');
        const discounted_price = use ? Number(calculatePriceWithCoupon(pkg, use.coupon.percentage, use?.coupon.reduction)) : base_price;
        const discount_amount = base_price - discounted_price;
        
        logEvent(this.ga, 'begin_checkout', {
          currency: 'EUR',
          value: Number(discounted_price.toFixed(2)),
          coupon: use?.code,
          items: [
            {
              item_id: pkg?.slug,
              item_name: humanize(pkg?.slug)!,
              coupon: use?.code,
              price: Number(discounted_price.toFixed(2)),
              discount: Number(discount_amount.toFixed(2)),
              quantity: 1
            }
          ]
        });
      }
    });

    this.code.valueChanges.pipe(debounceTime(500)).subscribe(code => {
      if(!code){
        this.selected_use_id.set(undefined);
        this.coupon_state.set('idle');
        return;
      }

      this.code.markAsTouched();

      const use = this.uses().find(use => use.code.toLowerCase() == code.toLowerCase());

      //if it's already in our list
      if(use){
        const pkg = this.packages().find(pkg => pkg._id == use.coupon.package);
        const same_package = use.coupon.package == this.package()?._id;
        //if it's the same package as the selected one
        if(same_package){
          this.selected_use_id.set(use._id);
          this.coupon_state.set('success');
        } else if(pkg && use.coupon.bypass) {
          this.changePlanPrompt(pkg, use);
        } else {
          this.coupon_state.set('failed');
          this.code.setErrors({
            coupon: 'Code is not for the given plan'
          });
          return;
        }
      } else {
        //new codes
        this.coupon_state.set('pending');
        this.isLoading.set(true);
        this.addCoupon();
      }
    });

    afterNextRender(() => {
      //start listening to plan parameter changes
      this.activated.params.pipe(
        takeUntilDestroyed(this.destroy),
        switchMap(({ plan }) => plan ? this.http.get<IPackage>(backend + 'package/info', { params: { slug: plan } }) : of(null)),
      ).subscribe({
        next: pkg => {
          if(pkg){
            this.package.set(pkg);
          } else {
            this.router.navigate(['/']);
          }
        },
        error: error => {
          console.warn(error);
          this.router.navigate(['/']);
        }
      });

      // Get all unused coupons
      this.profile.getCoupons(null, true).pipe(takeUntilDestroyed(this.destroy)).subscribe(({ uses, packages }) => {
        this.packages.set(packages);
        this.uses.set(uses);
        if(uses.length && !this.code.value && this.package()){
          const sorted = sortCouponsByValue(this.package()!,uses);
          if(sorted.length){
            this.code.setValue(sorted[0].code);
          }
        }
      });

      //start listening to code query parameter changes
      this.activated.queryParams.pipe(takeUntilDestroyed(this.destroy), filter(params => params.code)).subscribe(({code}) => {
        this.code.setValue(code);
        this.code.markAsTouched();
      });

      this.auth.fireState$.pipe(takeUntilDestroyed(this.destroy), debounceTime(500)).subscribe(() => {
        if(this.auth.isPremium()){
          this.router.navigate(['/profile']);
        }

      });
    });
  }

  addCoupon(code?: string){
    code = code || this.code.value;
    this.profile.addCoupon(code, this.plan()).pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: use => {
        const pkg = use.coupon.package;
        const actual_use: ICouponUse = { ...use, coupon: { ...use.coupon, package: pkg._id } };
        this.uses.update(uses => [...uses, actual_use]);
        if(pkg._id == this.package()?._id){
          this.selected_use_id.set(use._id);
          this.coupon_state.set('success');
        } else if(this.auth.isFree()) {
          this.changePlanPrompt(pkg, use);
        } else {
          this.coupon_state.set('failed');
          this.code.setErrors({
            coupon: 'Code provided is not the correct plan'
          });
        }
        this.isLoading.set(false);
      },
      error: error => {
        this.isLoading.set(false);
        if(error.message.includes('already added')){
          this.code.setValue(code);
        } else {
          this.coupon_state.set('failed');
          this.code.setErrors({
            coupon: error.message
          });
        }
      }
    });
  }

  openCouponSelector(){
    this.sheets.open(CouponListComponent, {
      closeOnNavigation: true,
      data: {
        uses: this.package_uses(),
        package: this.package()
      },
    }).afterDismissed().subscribe(choice => {
      if(choice || choice === ''){
        this.code.setValue(choice);
      } else if(this.package_uses().length && !this.code.value){
        const sorted = sortCouponsByValue(this.package()!,this.package_uses());
        if(sorted.length){
          this.code.setValue(sorted[0].code);
        }
      }
    });
  }

  changePlanPrompt(pkg: IPackage, use: ICouponUse){
    const bypass = use.coupon?.bypass;

    const title = pkg.package_type == 'corporate' ? 'Code is for provided plan' : 'Code is not for selected plan';
    const text = `${use ? 'The code you have entered' : 'You have a code that'} is for ${pkg.short_name ? 'the ' + pkg.short_name : 'a different'} plan. `;
    const question = bypass ? 'Would you like to <strong>activate</strong> it now for free?' : 'Would you like to switch to it?';

    const button = bypass ? 'Start Now' : 'Switch';

    this.confirm.open(title, text + question, 'Change Plan Checkout', [
      {
        label: 'No',
        close: true,
        paint: 'warn'
      },
      {
        label: button,
        close: true,
        paint: 'primary',
        callback: (ref) => {
          if(bypass){
            this.goBypass(use);
          } else {
            this.router.navigate(['/checkout', pkg.slug], { queryParams: { code: use.code } })
          }
        }
      }
    ]);
  }

  goBypass(use: ICouponUse){
    this.profile.useCorporate(use._id, this.router.isFromApp() ? 'app' : 'web').pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: result => {
        this.router.navigate(['/post-checkout'], { queryParams: { 
          p: use.coupon.package, 
          price: '0.00',
          code: use.code
        }});
      },
      error: error => {
        this.coupon_state.set('idle');
        this.code.reset('');
        this.isLoading.set(false);
      }
    })
  }

  onPayment(payment: { intent: PaymentIntent, payload: { package_id: string; use_id?: string; } }){
    // console.log(payment);
    this.profile.handleCheckout(payment.payload.package_id, payment.payload.use_id, payment.intent.id, payment.intent.created, false, this.router.isFromApp() ? 'App' : 'Web').subscribe({
      next: response => this.handleResponse(response),
      error: error => {
        error = error.error || error;
        this.toast.error(error?.message || 'Authorization failed to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
        this.isLoading.set(false);
        this.cardInput()?.loading.set(false);
      }
    });
  }

  goNext(package_id?: string, use_id?: string): void {

    if(!package_id){
      return;
    }

    if(this.block_transaction()){
      //!should not be able to reach this state since button is disabled
      return;
    }

    if(!this.terms.valid && !this.cartSummary()?.retailable()){
      this.terms.markAsTouched();
      return;
    }

    this.isLoading.set(true);

    const coupon_use = this.uses().find(u => u._id == use_id);
    const is_bypass = coupon_use && coupon_use.coupon && coupon_use.coupon.bypass;
    const card_input = this.cardInput();

    const from_app = this.router.isFromApp() ? 'App' : 'Web'; 

    if(card_input?.selected_view() == 'add' && !is_bypass){
      card_input.submitIntent(undefined, { package_id, use_id });
    } else {
      this.profile.handleCheckout(package_id, use_id, card_input?.selected_method_id() ?? undefined, Math.round(new Date().valueOf()/1000), is_bypass, from_app)
        .subscribe({
          next: response => this.handleResponse(response),
          error: error => {
            error = error.error || error;
            this.isLoading.set(false);
            card_input?.loading.set(false);
            if(error.code && error.code == 9){
              this.toast.error('Payment method is no longer valid and has been removed. Please enter a new one');
              card_input?.selected_view.set('add');
            } else {
              this.toast.error(error?.message || 'Authorization failed to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
            }
          }
        
        });
    }

  }


  handleResponse(result: IHandleResponse){
    const card_selector = this.cardInput();
    const cart = this.cartSummary();

    let status = result.status;
    
    if(status == 'ACTION_REQUIRED' && !result.payment_method){
      status = 'FAILED';
    } else if(result.client_secret){
      status = 'ACTION_REQUIRED';
    }

    switch(status){
      case 'COMPLETED': {

        const use = this.selected_use();
        const discounted_price = cart?.discounted_price();
        const discount_amount = cart?.discount_amount();
        const pkg = this.package();
        
        if('fbq' in window){
          try {
  
            let sub = this.auth.userSession();

            
            (window as any).fbq('track', 'Subscription', { 
              value: Number(discounted_price || '0.00'), 
              currency: 'EUR',
              subscription_sid: 'premium-' + (sub && sub._id || result._id),
              content_name: pkg?.name
            });
          } catch (error){
            console.warn('Failed to FBQ', (error as Error)?.message);
          }
        }
  
        const coupon = use?.coupon?.descriptor?.toLowerCase().replace(/[ -]/gm, '_');

        logEvent(this.ga, 'purchase', {
          currency: 'EUR',
          value: Number(Number(discounted_price || '0.00').toFixed(2)),
          transaction_id: result._id,
          ...coupon ? {
            coupon,
          } : {},
          items: [
            {
              item_id: pkg?.slug,
              item_name: pkg?.name || pkg?.slug,
              ...coupon ? {
                coupon,
              } : {},
              price: Number(Number(discounted_price || '0.00').toFixed(2)),
              discount: Number(Number(discount_amount || '0.00').toFixed(2)),
              quantity: 1
            }
          ]
        });

        this.router.navigate(['/post-checkout'], { queryParams: { 
          ...result.package ? { p: result.package } : {}, 
          slug: this.package()?.slug,
          price: discounted_price || '0.00',
          ...use ? { code: use.code } : {} 
        }});
        
        this.isLoading.set(false);
        card_selector?.loading.set(false);
        this.auth.sync();
        // this.message = 'Transaction was successful';
        break;
      }
      case 'ACTION_REQUIRED': {

        if(!result.client_secret || !['requires_payment_method', 'requires_confirmation', 'requires_action'].includes(result.intent_status || '')){
          this.toast.error('Transaction reached an invalid state. Please contact us on support@cloudigo.app or on Messenger.');
          break;
        }

        this.card.stripe.confirmCardPayment(result.client_secret, result.payment_method ? { payment_method: result.payment_method } : {}).pipe(
          switchMap(({ paymentIntent, error }) => {
            if(error) return throwError(() => error);
            else if(!paymentIntent) return throwError(() => new Error('No payment intent given'));
            else return this.profile.handleAction(result._id)
          })
        ).pipe(takeUntilDestroyed(this.destroy)).subscribe({
          next: response => this.handleResponse(response),
          error: error => {
            error = error.error || error;
            this.isLoading.set(false);
            card_selector?.loading.set(false);
            if(error.code && error.code == 9){
              this.toast.error('Payment method is no longer valid and has beend removed. Please enter a new one');
              card_selector?.selected_view.set('add');
            } else {
              this.toast.error(error?.message || 'Authorization failed to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
            }
          }
        });
          
        break;
      }
      default: {
        this.toast.error(result.error || 'Transaction failed to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
        this.isLoading.set(false);

        if(result.error == 'This PaymentMethod was previously used without being attached to a Customer or was detached from a Customer, and may not be used again.' || result.error == 'Chosen card is not valid. Please select another or add a new one'){
          this.cardInput()?.selected_view.set('add');
        } else {
          card_selector?.loading.set(false);
        }
        break;
      } 
    }

  }

}
